import { useState } from 'react';
import axiosInstance from './apiClient';

const useAxios = <T>(
  url: string,
  method: 'GET' | 'POST' | 'PUT' | 'DELETE' = 'GET',
  responseType: 'json' | 'blob' = 'json',
  transform?: (data: T) => T,
) => {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [errorStatus, setErrorStatus] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  const fetchData = async (body: any = null) => {
    setLoading(true);
    setError(null);
    setErrorStatus(null);
    try {
      const response = await axiosInstance({
        url,
        method,
        data: body,
        responseType,
      });

      if (transform) {
        const result = transform(response.data);
        setData(result);
      } else {
        setData(responseType === 'blob' ? (response.data as T) : response.data);
      }
      
      return response.data; // Return data for immediate use
    } catch (err: any) {
      // Don't throw the error, just set the state and return error info
      const errorMessage = err.response?.data?.message || err.message;
      const statusCode = err.response?.status;
      
      setError(errorMessage);
      setErrorStatus(`${statusCode}`);
      
      // Return error object instead of throwing
      return { 
        error: errorMessage, 
        status: statusCode,
        isError: true 
      };
    } finally {
      setLoading(false);
    }
  };

  const resetData = () => {
    setData(null);
    setError(null);
    setErrorStatus(null);
  };

  return { data, error, loading, fetchData, resetData, errorStatus };
};

export default useAxios;